import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { CartItem as CartItemComponent } from '../components/Cart/CartItem';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/useAuth'; // Import useAuth hook
import { placeOrder } from '../api/orderApi';

export const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { items, clear, total } = useCart();
  const { isAuthenticated } = useAuth(); // Get authentication state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpenConfirm = () => {
    if (items.length === 0) {
      setError('Your cart is empty');
      return;
    }
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/cart' } });
      return;
    }
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleCheckout = async () => {
    handleCloseConfirm();
    try {
      setLoading(true);
      // Prepare order format as per requirements
      const domain = window.location.hostname;
      const products = items.map(item => ({
        product_id: item.id,
        quantity: item.quantity,
        rate: item.retailer_rate || item.mrp || 0
      }));
      await placeOrder({ domain, products });
      clear();
      navigate('/orders');
    } catch (error) {
      console.error('Error placing order:', error);
      setError('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mb: isMobile ? 8 : 0 }}>
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Shopping Cart
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Your cart is empty
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/products')}
              sx={{ mt: 2 }}
            >
              Continue Shopping
            </Button>
          </Box>
        ) : (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ mb: 4 }}>
              {items.map((item) => (
                <CartItemComponent key={item.id} item={item} />
              ))}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box 
              sx={{ 
                mt: 3, 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'stretch' : 'center',
                gap: 2
              }}
            >
              <Typography variant="h5" sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                Total: ₹{total.toFixed(2)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/products')}
                  fullWidth={isMobile}
                >
                  Continue Shopping
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenConfirm}
                  disabled={loading}
                  fullWidth={isMobile}
                >
                  {loading ? <CircularProgress size={24} /> : 'Place Order'}
                </Button>

                {/* Confirmation Dialog */}
                <Dialog
                  open={openConfirm}
                  onClose={handleCloseConfirm}
                  aria-labelledby="alert-dialog-title"
                  aria-describedby="alert-dialog-description"
                >
                  <DialogTitle id="alert-dialog-title">
                    Confirm Order
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                      Are you sure you want to place this order?
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleCloseConfirm} color="primary">
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleCheckout} 
                      color="primary" 
                      autoFocus
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Confirm Order'}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </Container>
  );
};
